import { convertValueUnit } from '@metservicedev/cf-units';
import { CfJson } from '@metservicedev/platforms-services-interface/src/model/CfJson';
import { Duration, DateTime } from 'luxon';

import { convertNetcdfjsonToLineByLineDatumsWithoutMoment } from '../../../utils/convertNetcdfjsonToLineByLineDatum';
import reduceDatasetsByViewblockState from '../../../utils/reduceDatasetsByViewblockState';
import { RoseModelWithNoData, RoseState } from '../types';
import enrichVectors from './enrichVectors';
import pointsToCones from './pointsToCones';

const mergeTemplateAndData = (state: RoseState): RoseState => {
    const { datasets, viewblockState, time, capabilities } = state;
    if (!viewblockState?.databilities?.[0].capabilityId || !time) {
        return state;
    }

    const [{ capabilityId }] = viewblockState.databilities;
    const wholeCapability = capabilities.find(({ id }) => id === capabilityId);

    if (!wholeCapability) {
        return state;
    }
    const xVariableName = Object.keys(wholeCapability.variables).find((key) => wholeCapability.variables[key]?.attributes.axis === 'X');
    const yVariableName = Object.keys(wholeCapability.variables).find((key) => wholeCapability.variables[key]?.attributes.axis === 'Y');

    if (!xVariableName || !yVariableName) {
        console.error('Something is wrong with the capability definition');
        return state;
    }

    const { unit, timeRange, ...settings } = viewblockState.settings;
    const capabilityUnit = Object.values(wholeCapability.variables)[0]?.units;

    const isoDurations = timeRange.split('--');
    const luxonDurations = isoDurations.map((duration) => Duration.fromISO(duration));
    const [startTime, endTime] = luxonDurations.map((duration) => DateTime.fromISO(time.iso8601, { zone: time.timezone }).startOf('hour').plus(duration));

    const emptyModelState: RoseModelWithNoData = { settings, noData: true, unit };

    const { coneCount, binCount, binStepSize, label } = viewblockState.settings;
    if (!datasets) return { ...state, model: emptyModelState };
    if (datasets.length === 0) return { ...state, model: emptyModelState };
    const dataset = datasets[0]; // Rose can only handle a single dataset
    if (!dataset.cfjson) return { ...state, model: emptyModelState };
    if (!isVariableInCfjson(dataset.cfjson, xVariableName)) return { ...state, model: emptyModelState };
    if (!isVariableInCfjson(dataset.cfjson, yVariableName)) return { ...state, model: emptyModelState };

    const cleanDatasets = reduceDatasetsByViewblockState({ datasets: [dataset], viewblockState })
        .filter((dataset) => !dataset.isError)
        .map((dataset) => convertNetcdfjsonToLineByLineDatumsWithoutMoment({ netcdfjson: dataset.cfjson }))
        .filter((dataset) => !!dataset);

    if (!cleanDatasets.length) {
        return {
            ...state,
            model: emptyModelState,
        };
    }

    // Rose logic starts here
    // The Rose is about visualizing vector datasets by showing their magnitude, direction, and count.
    // We visualize these vectors using three dimensions:
    // Direction by the angles of the cones (north, northEast, east, eastSouth, etc...)
    // Magnitude by colour (binCount is the number of colours, binStepSize is the range for the colour)
    // Count by the length of the segment of the cone. The length from the center to the edge is the proportion of the count.

    // Our goal here is to ultimately convert our vector dataset into an object that is very simple for our renderer
    // First we must filter out the irrelevant times, in between startTime & endTime
    const series = cleanDatasets[0]!.filter(
        (datum) => startTime.valueOf() < DateTime.fromISO(datum.time).valueOf() && DateTime.fromISO(datum.time).valueOf() < endTime.valueOf()
    );

    // The settings has coneCount which is how many cones, or how many directions, we should be visualizing.
    // For each direction there will be a cone, and that cone is made up of all datapoints that go in that direction
    // However, the datapoints have different magnitude, to represent magnitudes we batch them into bins, using binStepSize

    // What our data looks like at the start:
    // [
    // 	{
    // 		time: iso8601DateTime...,
    // 		eastward_wind_etc_etc: 10,
    // 		norhward_wind_etc_etc: 10
    // 	},
    // 	{
    // 		time: iso8601DateTime...,
    // 		eastward_wind_etc_etc: 5,
    // 		norhward_wind_etc_etc: 3
    // 	},
    // 	...
    // ]

    //// We must go from this ^ to this v

    // Here is the JSON that our rose render needs:
    // [ // Each item inside this array is a cone, set by coneCount setting
    // 	{
    // 		id: 0,
    // 		angleRange: {min: -22.5, max: 22.5},
    // 		magnitudes: [10, 12, 4, 7] //the magnitudes of the vectors that fit within this angleRange
    // 		segments: [ // Each item inside this array is a bin, set by binCount setting
    // 			{
    // 				bin: 0, //The colour is based on this number, as it is what bin this is for
    // 				magnitudeRange: {min: 0, max: 2},
    // 				length: 0 //The length here is the proportion of the cone, all segment lengths added together should equal 1.
    // 			},
    // 			...
    // 		]
    // 	}
    // ]

    // Here is where we do the transformation from data to rose
    // const coneAngleSize = 360 / coneCount;

    // This section converts the vectors into points with angles and magnitudes

    // Add the magnitude and angle properties to each datum
    const enrichedDatums = enrichVectors(
        series.map((d) => ({ ...d, x: d[xVariableName], y: d[yVariableName] })),
        settings.direction
    )
        // apply the unit to the magnitude
        .map((datum) => ({ ...datum, magnitude: convertValueUnit({ from: capabilityUnit ?? '', to: unit, value: datum.magnitude }) as number }));

    const enrichedDatumsSorted = enrichedDatums.sort((a, b) => (a.magnitude < b.magnitude ? 1 : -1));
    const points = enrichedDatums.map((datum) => ({
        ...datum,
        normalizedMagnitude: datum.magnitude / enrichedDatumsSorted[0].magnitude,
    }));

    const cones = pointsToCones({ points, coneCount, binCount, binStepSize })
        // Just to be sure everything's in the right order, our cones are ordered clockwise North->east>south->etc, and true for any directional granularity.
        .sort((cone0, cone1) => {
            return cone0.id > cone1.id ? 1 : -1;
        });

    const maximumConeLength = cones
        .map((cone) => {
            return cone.segments.reduce((prev, current) => current.length + prev, 0);
        })
        .sort((a, b) => b - a)[0];
    const multiplicationRatio = !maximumConeLength || !viewblockState.settings.relativeFrequencyAxis ? 1 : 1 / maximumConeLength;

    const label2 =
        startTime && time.timezone && endTime
            ? `${DateTime.fromISO(startTime.toString(), { zone: time.timezone }).toLocaleString(DateTime.DATETIME_SHORT)} - ${DateTime.fromISO(
                  endTime.toString(),
                  {
                      zone: time.timezone,
                  }
              ).toLocaleString(DateTime.DATETIME_SHORT)} ${time.timezone}`
            : '';

    return {
        ...state,
        model: {
            cones,
            settings: viewblockState.settings,
            unit,
            points,
            multiplicationRatio,
            label: label || wholeCapability.display_name_long,
            label2,
        },
    };
};

export default mergeTemplateAndData;

const isVariableInCfjson = (cfjson: CfJson, variable: string) => {
    const standardNamedVariable = Object.values(cfjson.variables).find((v) => {
        if (v.attributes.standard_name === variable) return true;
        return false;
    });
    if (standardNamedVariable) return true;
    if (cfjson.variables[variable]) return true;
    return false;
};
